// src/pages/Details.jsx
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaArrowLeft, FaTimes } from "react-icons/fa";
import { catalog } from "../data/catalog";
import { useNavigate, useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import "./Details.css";

// ------------------------------
// SAFE TYPING HOOK
// ------------------------------
function useTyping(text, speed = 30) {
  const safeText = typeof text === "string" ? text : "";
  const [displayedText, setDisplayedText] = useState("");

  useEffect(() => {
    setDisplayedText("");
    let i = 0;

    function typeNext() {
      if (i < safeText.length) {
        setDisplayedText(prev => prev + (safeText[i] || ""));
        i++;
        setTimeout(typeNext, speed);
      }
    }

    typeNext();
    return () => { i = safeText.length; };
  }, [safeText, speed]);

  return displayedText;
}

export default function Details() {
  const navigate = useNavigate();
  const location = useLocation();
  const [item, setItem] = useState(null);
  const [activeSectionTitle, setActiveSectionTitle] = useState("");
  
  // NEW: State for full-screen modal media
  const [modalMedia, setModalMedia] = useState(null);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname, location.search]);

  // Load selected item from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const selected = params.get("selected");

    if (!selected) {
      setItem(null);
      return;
    }

    const found = catalog.find(
      c => c.title?.toLowerCase() === selected.toLowerCase()
    );

    setItem(found || null);
  }, [location]);

  // Intersection Observer to update the title dynamically based on scroll position inside sections
  useEffect(() => {
    const handleScrollTitle = () => {
      const galleryElement = document.getElementById("gallery");
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      if (galleryElement && scrollPosition >= galleryElement.offsetTop) {
        setActiveSectionTitle("Gallery");
      } else {
        setActiveSectionTitle("");
      }
    };

    window.addEventListener("scroll", handleScrollTitle);
    return () => window.removeEventListener("scroll", handleScrollTitle);
  }, []);

  // Clean & safe description
  const cleanDescription = item?.description
    ? String(item.description).replace(/^\n/, "")
    : "";

  const typedDescription = useTyping(cleanDescription, 20);

  // Clean features
  const cleanFeatures = Array.isArray(item?.features)
    ? item.features.filter(f => f?.toString().trim() !== "")
    : [];

  // Helper to ensure absolute image URLs for SEO
  const getAbsoluteImageUrl = (imgPath) => {
    if (!imgPath) return "https://ankielabs.com/images/anikielab.webp";
    if (imgPath.startsWith("http")) return imgPath;
    return `https://ankielabs.com${imgPath.startsWith("/") ? "" : "/"}${imgPath}`;
  };

  // If item not found
  if (!item) {
    return (
      <div className="details-page neon">
        <div className="details-inner">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back
          </button>
          <h2>Item not found</h2>
        </div>
      </div>
    );
  }

  const absoluteImage = getAbsoluteImageUrl(item.image);

  // Determine dynamic title based on item details and scroll position
  const getDynamicTitle = () => {
    if (!item) return "Ankielabs – Creative Production & 3D Services";
    
    if (activeSectionTitle === "Gallery") {
      return `${item.title} Gallery — Ankielabs`;
    }

    if (item.kind === "course") {
      return `${item.title} Courses — Ankielabs Training`;
    }
    if (item.kind === "service") {
      return `${item.title} Services — Ankielabs Creative`;
    }
    
    return `${item.title} — Ankielabs`;
  };

  return (
    <>
      {/* SEO Data automatically updated on scroll/navigation */}
      <SEO
        title={getDynamicTitle()}
        description={cleanDescription.slice(0, 160) || "Professional creative production services and courses by Ankielabs."}
        image={absoluteImage}
        url={typeof window !== "undefined" ? window.location.href : "https://ankielabs.com"}
        schemaType={item.kind === "course" ? "Course" : "Service"}
        schemaData={{
          name: item.title,
          description: cleanDescription,
          image: absoluteImage
        }}
      />

      <motion.div
        className="details-page neon"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="details-inner">

          {/* BACK BUTTON */}
          <button className="back-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back
          </button>

          {/* ---------- TOP VISUAL ---------- */}
          <div className="details-top">
            <div className="details-visual">

              {/* IMAGE OR VIDEO */}
              {item.image ? (
                item.image.endsWith(".webm") || item.image.endsWith(".mp4") ? (
                  <video
                    src={item.image}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="visual-media"
                  />
                ) : (
                  <img
                    src={item.image}
                    alt={item.title}
                    className="visual-media"
                    loading="lazy"
                  />
                )
              ) : (
                <div className="no-image">No Image</div>
              )}

              {/* BADGE */}
              {item.kind && (
                <span className={`badge ${item.kind}-badge`}>
                  {item.kind}
                </span>
              )}
            </div>

            {/* ---------- META INFO ---------- */}
            <div className="hero-meta">
              <h1>{item.title}</h1>

              {item.subtitle && (
                <p className="subtitle">{item.subtitle}</p>
              )}

              {item.fees && (
                <p className="fees">💰 Fees: {item.fees}</p>
              )}

              {typedDescription && (
                <p className="typed-description">{typedDescription}</p>
              )}

              {/* CONTACT BUTTON */}
              <button
                className="btn-primary"
                onClick={() =>
                  navigate(
                    `/?type=${item.kind}&title=${encodeURIComponent(item.title)}#contact`
                  )
                }
              >
                Contact / Select
              </button>
            </div>
          </div>

          {/* ---------- FEATURES ---------- */}
          {cleanFeatures.length > 0 && (
            <section>
              <h2>Highlights</h2>
              <div className="highlights">
                {cleanFeatures.map((f, i) => (
                  <div key={i}>{f}</div>
                ))}
              </div>
            </section>
          )}

          {/* ---------- GALLERY SECTION ---------- */}
          {item?.gallery?.length > 0 && (
            <section id="gallery" className="gallery-section">
              <h2 className="gallery-title">Gallery</h2>

              <div className="gallery-grid">
                {item.gallery.map((g, i) => (
                  <div 
                    key={i} 
                    className="gallery-item" 
                    onClick={() => setModalMedia(g)} // Click to open modal
                    style={{ cursor: "pointer" }}
                  >
                    {g.endsWith(".mp4") || g.endsWith(".webm") ? (
                      <video
                        src={g}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="gallery-media"
                      />
                    ) : (
                      <img
                        src={g}
                        alt={`gallery-${i}`}
                        className="gallery-media"
                        loading="lazy"
                      />
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

        </div>
      </motion.div>

      {/* ---------- FULL-SCREEN LIGHTBOX MODAL ---------- */}
      {modalMedia && (
        <div className="gallery-modal-overlay" onClick={() => setModalMedia(null)}>
          <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="gallery-modal-close" onClick={() => setModalMedia(null)}>
              <FaTimes />
            </button>
            {modalMedia.endsWith(".mp4") || modalMedia.endsWith(".webm") ? (
              <video
                src={modalMedia}
                controls
                autoPlay
                playsInline
                className="gallery-modal-media"
              />
            ) : (
              <img
                src={modalMedia}
                alt="Full Screen Preview"
                className="gallery-modal-media"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}